// Manual "Push to switch" and "Retire number" — SwitchNumberPushController
// (/v1/numbers/switch-push) and NumberInventoryController (/v1/numbers/{id}/retire).
//
// Push writes a number's label (ivr_dialing_info.business_id) on its HOME switch so
// it agrees with who holds the number in cpass. Preview first: apply sends back the
// preview's hash, and the server refuses (409) if anything moved in between.
//
// Every shape here is camelCase: these are Java records and a plain entity on the
// server, unlike the snake_case ProviderNumber rows beside them.
import api from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { responseFrom, errorFrom } from '../apiEnvelope';
import type { APIResponse } from 'types/ivr';
import type { ProviderNumber } from './providers.service';

/** cpass.numbers.switch-push.mode. Only APPLY writes; OFF refuses even a preview. */
export type SwitchPushMode = 'OFF' | 'PREVIEW' | 'APPLY';
/** CREATE = a missing row inserted with a chosen flow; SETTINGS = per-number columns (Number details → Settings). */
export type SwitchPushAction = 'ASSIGN' | 'PARK' | 'NO_CHANGE' | 'BLOCKED' | 'ROLLBACK' | 'CREATE' | 'SETTINGS';

/** An explicit flow choice as the server resolved it on the home switch. */
export interface SwitchFlowChoice {
  operatorId: string;
  serviceId: string;
  exists: boolean;
  /** state_details.business_name of the flow: the S3 folder recordings are filed under. */
  recordingFolders: string[];
}

/** What a CREATE would insert besides label and flow. */
export interface SwitchCreateSpec {
  cliNumber: string;
  spellingRule: string;
  dialingCode: string;
  transcribeName: string;
  defaultLanguage: string;
  defaultsSource: string;
}
export type SwitchPushStatus = 'OK' | 'FAILED' | 'SKIPPED';

/** One ivr_dialing_info row. webhookConfig is masked to "(set)" by the server. */
export interface SwitchRow {
  id: number;
  cliNumber: string;
  businessId?: string | null;
  businessName?: string | null;
  operatorId?: string | null;
  serviceId?: string | null;
  webhookConfig?: string | null;
}

/** Rows for the same DID on a switch that is NOT its home — reported, never written. */
export interface SwitchResidue {
  switchName: string;
  rows: SwitchRow[];
  error?: string | null;
}

export interface SwitchPushPlan {
  numberId: number;
  e164: string;
  providerId?: number | null;
  providerName?: string | null;
  switchSourceId?: string | null;
  switchName?: string | null;
  homeRows: SwitchRow[];
  residue: SwitchResidue[];
  holderOrganizationId?: string | null;
  holderName?: string | null;
  holderBusinessId?: string | null;
  action: SwitchPushAction;
  target?: SwitchRow | null;
  newBusinessId?: string | null;
  newBusinessName?: string | null;
  newServiceId?: string | null;
  reason: string;
  warnings: string[];
  /** Set only when the flow changes (or on CREATE). */
  newOperatorId?: string | null;
  create?: SwitchCreateSpec | null;
  flow?: SwitchFlowChoice | null;
}

export interface SwitchPushPreview {
  mode: SwitchPushMode;
  previewHash: string;
  applicable: number;
  plans: SwitchPushPlan[];
}

export interface SwitchPushResult {
  numberId: number;
  e164: string;
  action: SwitchPushAction;
  status: SwitchPushStatus;
  detail?: string | null;
  pushId?: number | null;
  cacheRefreshed?: boolean | null;
}

export interface SwitchPushApplyResult {
  mode: SwitchPushMode;
  previewHash: string;
  ok: number;
  failed: number;
  skipped: number;
  results: SwitchPushResult[];
}

/** One audit row of switch_number_pushes. */
export interface SwitchPushRecord {
  id: number;
  providerNumberId?: number | null;
  e164: string;
  providerId?: number | null;
  switchSourceId?: string | null;
  switchRowId?: number | null;
  switchCliNumber?: string | null;
  action: SwitchPushAction;
  oldBusinessId?: string | null;
  oldBusinessName?: string | null;
  oldServiceId?: string | null;
  newBusinessId?: string | null;
  newBusinessName?: string | null;
  newServiceId?: string | null;
  /** Only when the push changed the flow or created the row. */
  oldOperatorId?: string | null;
  newOperatorId?: string | null;
  holderOrganizationId?: string | null;
  status: SwitchPushStatus;
  detail?: string | null;
  cacheRefreshed?: boolean | null;
  rollbackOf?: number | null;
  requestedBy: string;
  /** Epoch millis or ISO. */
  createdAt: number | string;
}

export interface SwitchPushHistoryPage {
  content: SwitchPushRecord[];
  totalElements?: number;
  totalPages?: number;
  number?: number;
}

/** What retire did on the switch side: nothing (off), a preview, or a preview plus the apply. */
export interface RetireSwitchOutcome {
  mode: SwitchPushMode;
  note?: string;
  error?: string;
  preview?: SwitchPushPreview;
  applied?: SwitchPushApplyResult;
}

export interface RetireResult {
  number: ProviderNumber;
  switch: RetireSwitchOutcome;
}

export const switchPushService = {
  mode: async (): Promise<APIResponse<{ mode: SwitchPushMode }>> => {
    try { return responseFrom<{ mode: SwitchPushMode }>((await api.get(API_ENDPOINTS.SWITCH_PUSH_MODE)).data); }
    catch (e: any) { return errorFrom<{ mode: SwitchPushMode }>(e, 'Could not read the switch-push mode'); }
  },

  /** Reads both switches and decides per number. Writes nothing. */
  preview: async (numberIds: number[]): Promise<APIResponse<SwitchPushPreview>> => {
    try { return responseFrom<SwitchPushPreview>((await api.post(API_ENDPOINTS.SWITCH_PUSH_PREVIEW, { numberIds })).data); }
    catch (e: any) { return errorFrom<SwitchPushPreview>(e, 'Could not preview the switch push'); }
  },

  /** Writes the previewed plan. Refused (409) unless the mode is APPLY and nothing changed since the preview. */
  apply: async (previewHash: string, numberIds: number[]): Promise<APIResponse<SwitchPushApplyResult>> => {
    try {
      return responseFrom<SwitchPushApplyResult>(
        (await api.post(API_ENDPOINTS.SWITCH_PUSH_APPLY, { previewHash, numberIds })).data);
    } catch (e: any) { return errorFrom<SwitchPushApplyResult>(e, 'Could not apply the switch push'); }
  },

  history: async (e164: string, page = 0): Promise<APIResponse<SwitchPushHistoryPage>> => {
    try {
      return responseFrom<SwitchPushHistoryPage>(
        (await api.get(API_ENDPOINTS.SWITCH_PUSH_HISTORY, { params: { e164, page } })).data);
    } catch (e: any) { return errorFrom<SwitchPushHistoryPage>(e, 'Could not load the switch history'); }
  },

  /** Restores a push's old values, only if the switch row still holds what was pushed. */
  rollback: async (pushId: number): Promise<APIResponse<SwitchPushRecord>> => {
    try { return responseFrom<SwitchPushRecord>((await api.post(API_ENDPOINTS.SWITCH_PUSH_ROLLBACK(pushId))).data); }
    catch (e: any) { return errorFrom<SwitchPushRecord>(e, 'Could not roll back this push'); }
  },

  /** Closes the tenure, marks RETIRED (never deleted), then parks on the switch if push is in apply mode. */
  retire: async (numberId: number, reason: string): Promise<APIResponse<RetireResult>> => {
    try {
      return responseFrom<RetireResult>((await api.post(API_ENDPOINTS.NUMBER_RETIRE(numberId), { reason })).data);
    } catch (e: any) { return errorFrom<RetireResult>(e, 'Could not retire the number'); }
  },

  /** Back to the pool as AVAILABLE, with no holder. The switch row is not touched. */
  unretire: async (numberId: number): Promise<APIResponse<ProviderNumber>> => {
    try { return responseFrom<ProviderNumber>((await api.post(API_ENDPOINTS.NUMBER_UNRETIRE(numberId))).data); }
    catch (e: any) { return errorFrom<ProviderNumber>(e, 'Could not unretire the number'); }
  },
};

export default switchPushService;
